import type AttachmentWellViewState from '../../schema/AttachmentWellViewState';
import type { CreateAttachmentsProperties } from '../../types/CreateAttachmentsProperties';
import { AttachmentFile, AttachmentFileType } from 'owa-attachment-file-types';
import {
    lazyCreateAttachmentsViaQueueManager,
    lazyCreateAttachmentFileFromExistingMailboxAttachment,
    OnAttachmentCreatedCallback,
    OnAttachmentCanceledOrFailedCallback,
} from '../../index';
import type { ClientItemId } from 'owa-client-ids';
import type { InfoBarHostViewState } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import type AttachmentType from 'owa-service/lib/contract/AttachmentType';
import { isAttachmentOfReferenceType } from 'owa-attachment-type/lib/isAttachmentOfReferenceType';

export default async function createAttachmentsFromRecentAttachments(
    itemId: ClientItemId,
    attachmentWell: AttachmentWellViewState,
    recentAttachments: AttachmentType[],
    infoBar: InfoBarHostViewState,
    onAttachmentCreatedCallback?: OnAttachmentCreatedCallback,
    onAttachmentCanceledOrFailedCallback?: OnAttachmentCanceledOrFailedCallback
) {
    const createAttachmentFileFromAttachmentType = await lazyCreateAttachmentFileFromExistingMailboxAttachment.import();
    const attachmentFiles: AttachmentFile[] = recentAttachments
        .filter(a => !isClassicInlineAttachment(a))
        .map(createAttachmentFileFromAttachmentType);
    const cloudFiles = attachmentFiles.filter(a => a.fileType === AttachmentFileType.Cloud);
    const attachmentItemFiles = attachmentFiles.filter(
        a => a.fileType === AttachmentFileType.AttachmentItem
    );
    attachmentItemFiles.length > 0 &&
        lazyCreateAttachment(
            attachmentWell,
            itemId,
            attachmentItemFiles,
            infoBar,
            false /* isInline */,
            false /* shouldShare */,
            onAttachmentCreatedCallback,
            onAttachmentCanceledOrFailedCallback
        );

    cloudFiles.length > 0 &&
        lazyCreateAttachment(
            attachmentWell,
            itemId,
            cloudFiles,
            infoBar,
            false /* isInline */,
            true /* shouldShare */,
            onAttachmentCreatedCallback,
            onAttachmentCanceledOrFailedCallback
        );
}

function isClassicInlineAttachment(attachmentType: AttachmentType) {
    return !isAttachmentOfReferenceType(attachmentType) && attachmentType.IsInline;
}

function lazyCreateAttachment(
    attachmentWell: AttachmentWellViewState,
    itemId: ClientItemId,
    attachments: AttachmentFile[],
    infoBar: InfoBarHostViewState,
    isInline: boolean,
    shouldShare: boolean,
    onAttachmentCreatedCallback?: OnAttachmentCreatedCallback,
    onAttachmentCanceledOrFailedCallback?: OnAttachmentCanceledOrFailedCallback
) {
    const baseProperties = {
        isInline,
        isHiddenInline: false,
    };
    const properties: CreateAttachmentsProperties = shouldShare
        ? {
              ...baseProperties,
              shouldShare: true,
          }
        : { ...baseProperties, shouldShare: false };

    lazyCreateAttachmentsViaQueueManager.import().then(createAttachmentsViaQueueManager => {
        createAttachmentsViaQueueManager(
            attachmentWell,
            itemId,
            attachments,
            infoBar,
            properties,
            null /*onFinish */,
            onAttachmentCreatedCallback,
            onAttachmentCanceledOrFailedCallback
        );
    });
}
